import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../components/Header'
import Toast from '../components/Toast'
import { authAPI, teamsAPI, stationAPI } from '../utils/api'
import ScoreForm from '../components/ScoreForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHouse, faChartLine, faKey, faCircleInfo, faPeopleGroup } from '@fortawesome/free-solid-svg-icons'

function Dashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('home')
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [teams, setTeams] = useState([])
  const [stations, setStations] = useState([])
  const [toast, setToast] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (user?.requiresPasswordChange) {
      setShowPasswordModal(true)
    }
    loadTeamsAndStations()
  }, [user])

  const loadTeamsAndStations = async () => {
    try {
      const [teamsRes, stationsRes] = await Promise.all([
        teamsAPI.getAll(),
        stationAPI.getAll(),
      ])
      setTeams(teamsRes.data)
      setStations(stationsRes.data.filter(s => !s.deleted))
    } catch (error) {
      console.error('Error loading teams/stations:', error)
    }
  }

  const handleScoreSubmit = (data) => {
    setToast({ message: '✓ Score submitted!', type: 'success' })
    setTimeout(() => setToast(null), 3000)
  }

  return (
    <div>
      <Header user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        <section className="rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white p-6 md:p-8 shadow-2xl overflow-hidden relative">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_rgba(59,130,246,0.5),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(236,72,153,0.35),_transparent_30%)]" />
          <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold">
                <FontAwesomeIcon icon={faHouse} />
                Home
              </div>
              <div>
                <h1 className="text-3xl md:text-5xl font-black leading-tight mb-3">
                  Welcome back, {user.name}
                </h1>
                <p className="text-slate-200 text-base md:text-lg">
                  This is your home page. Change your password here, jump to scores, and check the leaderboard from one place.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 min-w-[260px]">
              <div className="rounded-2xl bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-wider text-slate-300">Role</p>
                <p className="text-xl font-black">{user.role === 'staff_station' ? 'Staff + Station' : user.role}</p>
              </div>
              <div className="rounded-2xl bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-wider text-slate-300">Teams</p>
                <p className="text-2xl font-black">{user.teams?.length || 0}</p>
              </div>
              <div className="rounded-2xl bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-wider text-slate-300">Stations</p>
                <p className="text-2xl font-black">{user.stations?.length || 0}</p>
              </div>
              <div className="rounded-2xl bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-wider text-slate-300">Status</p>
                <p className="text-2xl font-black">Ready</p>
              </div>
            </div>
          </div>
        </section>

        <section className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => setActiveTab('home')}
            className={`rounded-2xl border p-5 text-left transition shadow-sm hover:shadow-lg ${
              activeTab === 'home' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'
            }`}
          >
            <div className="h-11 w-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faHouse} />
            </div>
            <h2 className="text-xl font-black text-slate-900">Home</h2>
            <p className="text-sm text-slate-600 mt-1">Password, status, and quick actions.</p>
          </button>
          <button
            onClick={() => setActiveTab('scores')}
            className={`rounded-2xl border p-5 text-left transition shadow-sm hover:shadow-lg ${
              activeTab === 'scores' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'
            }`}
          >
            <div className="h-11 w-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <h2 className="text-xl font-black text-slate-900">Add Score</h2>
            <p className="text-sm text-slate-600 mt-1">Open score entry quickly.</p>
          </button>
          <button
            onClick={() => navigate('/leaderboard')}
            className="rounded-2xl border p-5 text-left transition shadow-sm hover:shadow-lg border-slate-200 bg-white"
          >
            <div className="h-11 w-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center mb-4">
              <FontAwesomeIcon icon={faPeopleGroup} />
            </div>
            <h2 className="text-xl font-black text-slate-900">Leaderboard</h2>
            <p className="text-sm text-slate-600 mt-1">View live rankings and logs.</p>
          </button>
        </section>

        <div className="flex gap-4 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('home')}
            className={`px-4 py-2 rounded font-semibold whitespace-nowrap ${
              activeTab === 'home' ? 'bg-blue-600 text-white' : 'bg-gray-200'
            }`}
          >
            Home
          </button>
          <button
            onClick={() => setActiveTab('scores')}
            className={`px-4 py-2 rounded font-semibold whitespace-nowrap ${
              activeTab === 'scores' ? 'bg-blue-600 text-white' : 'bg-gray-200'
            }`}
          >
            Add Score
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`px-4 py-2 rounded font-semibold whitespace-nowrap ${
              activeTab === 'leaderboard' ? 'bg-blue-600 text-white' : 'bg-gray-200'
            }`}
          >
            Leaderboard
          </button>
        </div>

        {activeTab === 'home' && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="card lg:col-span-2">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faCircleInfo} />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-black mb-2">Home overview</h2>
                  <p className="text-gray-600 mb-4">
                    {user.name} <span className="text-xl">({user.nickname})</span>
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="rounded-2xl bg-blue-50 p-4">
                      <p className="text-xs uppercase tracking-wider text-blue-700">Role</p>
                      <p className="text-lg font-bold text-slate-900 mt-1">{user.role === 'staff_station' ? 'Staff + Station' : user.role}</p>
                    </div>
                    <div className="rounded-2xl bg-emerald-50 p-4">
                      <p className="text-xs uppercase tracking-wider text-emerald-700">Teams</p>
                      <p className="text-lg font-bold text-slate-900 mt-1">{user.teams?.length || 0}</p>
                    </div>
                    <div className="rounded-2xl bg-amber-50 p-4">
                      <p className="text-xs uppercase tracking-wider text-amber-700">Stations</p>
                      <p className="text-lg font-bold text-slate-900 mt-1">{user.stations?.length || 0}</p>
                    </div>
                  </div>

                  {user?.teams?.length > 0 ? (
                    <p className="mt-4 text-gray-600">
                      You can submit scores immediately from Add Score.
                    </p>
                  ) : (
                    <p className="mt-4 text-gray-600">
                      กรุณารอให้ Admin กำหนดบทบาทและทีมให้คุณ
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => setShowPasswordModal(true)}
                className="card w-full text-left border border-slate-200 hover:border-blue-400 hover:shadow-lg transition"
              >
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center">
                    <FontAwesomeIcon icon={faKey} />
                  </div>
                  <div>
                    <h3 className="font-black">Change Password</h3>
                    <p className="text-sm text-gray-500">Update your login password here</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate('/leaderboard')}
                className="card w-full text-left border border-slate-200 hover:border-blue-400 hover:shadow-lg transition"
              >
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-2xl bg-blue-600 text-white flex items-center justify-center">
                    <FontAwesomeIcon icon={faChartLine} />
                  </div>
                  <div>
                    <h3 className="font-black">Live Leaderboard</h3>
                    <p className="text-sm text-gray-500">See rankings and score history</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {activeTab === 'scores' && (user.teams?.length > 0) && (
          <div className="card">
            <h2 className="text-2xl font-bold mb-6">Add Score</h2>
            <ScoreForm
              teams={teams}
              stations={stations}
              userRole={user.role}
              userStations={user.stations}
              onSubmit={handleScoreSubmit}
            />
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div className="card">
            <h2 className="text-2xl font-bold mb-6">Leaderboard</h2>
            <p className="text-gray-600">See the main leaderboard page for live updates</p>
            <button
              onClick={() => navigate('/leaderboard')}
              className="btn btn-primary mt-4"
            >
              View Leaderboard →
            </button>
          </div>
        )}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {showPasswordModal && (
          <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
        )}
      </div>
    </div>
  )
}

function ChangePasswordModal({ onClose }) {
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setLoading(true)
    try {
      await authAPI.changePassword(oldPassword, newPassword)
      setSuccess(true)
      setTimeout(onClose, 2000)
    } catch (err) {
      setError(err.response?.data?.error || 'Password change failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
        <h2 className="text-2xl font-bold mb-4">Change Password</h2>

        {success ? (
          <div className="bg-green-50 p-4 rounded text-green-700">
            ✓ Password changed successfully!
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 p-3 rounded text-red-700 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Old Password
              </label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                required
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary flex-1"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default Dashboard
